import { useState } from 'react'
import {
  type ApiTokenView,
  type MintedToken,
  scopeGroups,
  tokenActions,
  useAdminApiTokens,
  useCreateApiToken,
  useDeleteApiToken,
  useRevokeApiToken,
  useRotateApiToken,
  useUpdateApiTokenScopes,
} from '../../../features/admin/use-admin-api-tokens'
import { useAdminPermissions } from '../../../features/admin/use-admin-permissions'
import { AdminPage, adminError, td } from './admin-bits'
import { Btn, ConfirmDialog, Dialog, Field, Inline, input, Tag, th } from './controls'
import { mono, Note, Section, serif } from './ui'

function ScopePicker({ selected, onChange }: { selected: string[]; onChange: (next: string[]) => void }) {
  const perms = useAdminPermissions()
  const groups = scopeGroups(perms.data?.permissions ?? [], selected)
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id])
  return (
    <div className="max-h-64 overflow-auto border-y border-[var(--color-border)] py-3">
      {groups.length === 0 ? (
        <p className="text-[13px] italic text-[var(--color-muted)]">No scopes available.</p>
      ) : null}
      {groups.map((g) => (
        <fieldset key={g.groupTag} className="mb-3 last:mb-0">
          <legend className="mb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--color-muted)]">
            {g.groupTag}
          </legend>
          {g.scopes.map((s) => (
            <label key={s.id} className="flex cursor-pointer items-center gap-2 py-0.5 text-[13px]">
              <input
                type="checkbox"
                className="size-4 accent-[var(--color-accent)]"
                checked={selected.includes(s.id)}
                onChange={() => toggle(s.id)}
              />
              <span style={mono}>{s.name}</span>
            </label>
          ))}
        </fieldset>
      ))}
    </div>
  )
}

/** Shown once, from component state only. Closing discards it. */
function RawTokenDialog({ minted, onClose }: { minted: MintedToken; onClose: () => void }) {
  const [copied, setCopied] = useState(false)
  return (
    <Dialog
      open
      eyebrow="Shown once"
      title="Copy your token now"
      onClose={onClose}
      footer={
        <Btn variant="primary" onClick={onClose}>
          I've saved it
        </Btn>
      }
    >
      <p className="text-[15px] leading-[1.55] text-[var(--color-muted)]" style={serif}>
        This is the only time the token for <strong className="text-[var(--color-fg)]">{minted.token.name}</strong> is
        shown. It can't be retrieved later.
      </p>
      <code
        className="block break-all border-y border-[var(--v3-rule-strong)] bg-[var(--color-surface-2)] px-3 py-3 text-[13px]"
        style={mono}
      >
        {minted.rawToken}
      </code>
      <div>
        <Btn
          onClick={() => {
            navigator.clipboard
              ?.writeText(minted.rawToken)
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }}
        >
          {copied ? 'Copied' : 'Copy to clipboard'}
        </Btn>
      </div>
    </Dialog>
  )
}

function CreateDialog({ onClose, onMinted }: { onClose: () => void; onMinted: (m: MintedToken) => void }) {
  const create = useCreateApiToken()
  const [name, setName] = useState('')
  const [scopes, setScopes] = useState<string[]>([])
  const [expires, setExpires] = useState('')
  const valid = name.trim() !== '' && scopes.length > 0
  const submit = () =>
    create.mutate(
      {
        name: name.trim(),
        scopes,
        expiresAt: expires ? new Date(`${expires}T23:59:59Z`).toISOString() : null,
      },
      { onSuccess: onMinted },
    )
  return (
    <Dialog
      open
      eyebrow="New entry"
      title="Create an API token"
      onClose={onClose}
      onSubmit={() => valid && !create.isPending && submit()}
      footer={
        <>
          <Btn onClick={onClose} disabled={create.isPending}>
            Cancel
          </Btn>
          <Btn variant="primary" disabled={!valid || create.isPending} onClick={submit}>
            {create.isPending ? 'Creating…' : 'Create token'}
          </Btn>
        </>
      }
    >
      <Field label="Name">
        <input className={input} value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <Field label="Expires" hint="optional">
        <input className={input} type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
      </Field>
      <div>
        <span className="mb-1 block text-[12px] text-[var(--color-muted)]">Scopes (at least one)</span>
        <ScopePicker selected={scopes} onChange={setScopes} />
      </div>
      {create.error ? <Inline tone="bad">{adminError(create.error)}</Inline> : null}
    </Dialog>
  )
}

function ScopesDialog({ token, onClose }: { token: ApiTokenView; onClose: () => void }) {
  const upd = useUpdateApiTokenScopes()
  const [scopes, setScopes] = useState(token.scopes)
  return (
    <Dialog
      open
      eyebrow="Amend"
      title={`Scopes for ${token.name}`}
      onClose={onClose}
      footer={
        <>
          <Btn onClick={onClose} disabled={upd.isPending}>
            Cancel
          </Btn>
          <Btn
            variant="primary"
            disabled={scopes.length === 0 || upd.isPending}
            onClick={() => upd.mutate({ id: token.id, scopes }, { onSuccess: onClose })}
          >
            {upd.isPending ? 'Saving…' : 'Save scopes'}
          </Btn>
        </>
      }
    >
      <ScopePicker selected={scopes} onChange={setScopes} />
      {upd.error ? <Inline tone="bad">{adminError(upd.error)}</Inline> : null}
    </Dialog>
  )
}

type Pending = { kind: 'rotate' | 'revoke' | 'delete'; token: ApiTokenView }

const statusTone = (s: ApiTokenView['status']) => (s === 'active' ? 'ok' : s === 'expired' ? 'warn' : 'off')
const fmt = (iso: string | null) => (iso ? iso.slice(0, 10) : '-')

const COPY = {
  rotate: ['Rotate this token?', 'A new secret is issued and the old one stops working immediately.', 'Rotate'],
  revoke: ['Revoke this token?', 'It stops working immediately. You can delete it afterwards.', 'Revoke'],
  delete: ['Delete this token?', 'The revoked token record is permanently removed.', 'Delete'],
} as const

export function LedgerAdminApiTokens() {
  const tokens = useAdminApiTokens()
  const rotate = useRotateApiToken()
  const revoke = useRevokeApiToken()
  const del = useDeleteApiToken()
  const [creating, setCreating] = useState(false)
  const [minted, setMinted] = useState<MintedToken | null>(null)
  const [scopesFor, setScopesFor] = useState<ApiTokenView | null>(null)
  const [pending, setPending] = useState<Pending | null>(null)
  const list = tokens.data ?? []
  const m = pending?.kind === 'rotate' ? rotate : pending?.kind === 'revoke' ? revoke : del
  const close = () => {
    m.reset()
    setPending(null)
  }
  const confirm = () => {
    if (!pending) return
    const { id } = pending.token
    if (pending.kind === 'rotate')
      rotate.mutate(
        { id },
        {
          onSuccess: (r) => {
            setPending(null)
            setMinted(r)
          },
        },
      )
    else (pending.kind === 'revoke' ? revoke : del).mutate({ id }, { onSuccess: () => setPending(null) })
  }
  return (
    <AdminPage
      title="API tokens"
      standfirst="Machine-to-machine access for services and scripts. A token's secret is shown once, when it is issued."
      loading={tokens.isLoading}
      error={!!tokens.error}
      aside={
        <Btn variant="primary" onClick={() => setCreating(true)}>
          Create token
        </Btn>
      }
    >
      <Section title={`${list.length} ${list.length === 1 ? 'token' : 'tokens'}`}>
        {list.length === 0 ? (
          <Note>No API tokens yet.</Note>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={th}>Name</th>
                  <th className={th}>Prefix</th>
                  <th className={th}>Scopes</th>
                  <th className={th}>Status</th>
                  <th className={th}>Expires</th>
                  <th className={th}>Last used</th>
                  <th className={`${th} text-right`}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t) => {
                  const a = tokenActions(t)
                  return (
                    <tr key={t.id}>
                      <td className={`${td} text-[16px] font-semibold`} style={serif}>
                        {t.name}
                      </td>
                      <td className={`${td} text-[12.5px]`} style={mono}>
                        {t.prefixDisplay}
                      </td>
                      <td className={`${td} tabular-nums text-[var(--color-muted)]`}>{t.scopes.length}</td>
                      <td className={td}>
                        <Tag tone={statusTone(t.status)}>{t.status}</Tag>
                      </td>
                      <td className={`${td} text-[12.5px]`} style={mono}>
                        {fmt(t.expiresAt)}
                      </td>
                      <td className={`${td} text-[12.5px]`} style={mono}>
                        {fmt(t.lastSeenAt)}
                      </td>
                      <td className={`${td} whitespace-nowrap text-right`}>
                        <Btn disabled={t.status === 'revoked'} onClick={() => setScopesFor(t)}>
                          Scopes
                        </Btn>{' '}
                        {a.canRotate ? (
                          <Btn onClick={() => setPending({ kind: 'rotate', token: t })}>Rotate</Btn>
                        ) : null}{' '}
                        {a.canRevoke ? (
                          <Btn variant="danger" onClick={() => setPending({ kind: 'revoke', token: t })}>
                            Revoke
                          </Btn>
                        ) : null}{' '}
                        {a.canDelete ? (
                          <Btn variant="danger" onClick={() => setPending({ kind: 'delete', token: t })}>
                            Delete
                          </Btn>
                        ) : null}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </Section>
      {creating ? (
        <CreateDialog
          onClose={() => setCreating(false)}
          onMinted={(r) => {
            setCreating(false)
            setMinted(r)
          }}
        />
      ) : null}
      {scopesFor ? <ScopesDialog token={scopesFor} onClose={() => setScopesFor(null)} /> : null}
      {minted ? <RawTokenDialog minted={minted} onClose={() => setMinted(null)} /> : null}
      <ConfirmDialog
        open={pending !== null}
        title={pending ? COPY[pending.kind][0] : ''}
        body={pending ? `${pending.token.name}: ${COPY[pending.kind][1]}` : null}
        confirmLabel={pending ? COPY[pending.kind][2] : ''}
        danger={pending?.kind !== 'rotate'}
        pending={m.isPending}
        error={m.error ? adminError(m.error) : null}
        onConfirm={confirm}
        onClose={close}
      />
    </AdminPage>
  )
}
